// ロビーの通信チェック（spec.md「ロビーでの通信チェック」）
export type NetQuality = 'good' | 'warn' | 'bad';

/** rtt＝画面に出す往復の時間（直近で遅かったときの値。言葉はこの数字だけで決める）。rttMedian・jitter は判定窓などの内部用 */
export type NetStats = { quality: NetQuality; rtt: number; rttMedian: number; jitter: number; lost: number };

/** 言葉の境目（目安の表と同じ。2026-10-11） */
export const NET_LIMITS = { fast: 100, normal: 200, slow: 400, lost: 2 } as const;

export function median(xs: number[]): number {
  if (xs.length === 0) return 0;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
}

/**
 * 直近10回分の rtt（途切れは null）から判定する（2026-10-11 見直し）。
 * 画面に出す数字（rtt）は「直近で遅かったときの往復の時間」：5回以上測れたら2番目に遅い値（たまの1回だけの遅れは除く）、
 * それより少なければいちばん遅い値。揺れ（遅くなったり速くなったり）もこの数字に表れる。
 * 言葉はこの数字だけで決める（目安の表どおり）：200ms未満は良好、200〜399ms は遅め、400ms以上は不安定。
 * 途切れ（返事が来ない）は10回中2回以上で不安定。1回だけは数えない
 */
export function classifyNet(samples: (number | null)[]): NetStats {
  const recent = samples.slice(-10);
  const ok = recent.filter((x): x is number => x !== null);
  const lost = recent.length - ok.length;
  const rttMedian = Math.round(median(ok));
  const sorted = [...ok].sort((a, b) => a - b);
  const rtt = sorted.length
    ? Math.round(sorted.length >= 5 ? sorted[sorted.length - 2]! : sorted[sorted.length - 1]!)
    : 0;
  const jitter = sorted.length ? Math.round(rtt - sorted[0]!) : 0;
  let quality: NetQuality = 'good';
  if (rtt >= NET_LIMITS.normal) quality = 'warn';
  if (rtt >= NET_LIMITS.slow || lost >= NET_LIMITS.lost || (ok.length === 0 && recent.length > 0))
    quality = 'bad';
  return { quality, rtt, rttMedian, jitter, lost };
}

export const NET_MESSAGES: Record<NetQuality, string> = {
  good: '通信は良好です',
  warn: '通信が遅めです。押した順番が正しく判定されないことがあります',
  bad: '通信が不安定です',
};

export const NET_ADVICE =
  'Wi-Fiと携帯回線を切り替えるか、電波のよい場所に移ってください。改善しないときは、別のスマホで参加することもできます';

/** 判定窓：片道の最大値＋30ms を 150〜300ms に収める。不安定の人は含めない */
export function judgeWindowFor(oneWayMs: number[], baseMs = 150): number {
  const max = oneWayMs.length ? Math.max(...oneWayMs) : 0;
  return Math.round(Math.min(300, Math.max(baseMs, max + 30)));
}

/**
 * 通信の速さの目安（2026-10-11）：往復の時間（classifyNet の rtt）を数字と言葉で出す。
 * 早押しで効くのは回線の太さではなく、押してからサーバーに届くまでの時間なので、往復の時間で示す。
 * 言葉は数字だけで決まる（目安の表と必ず一致する）。途切れで不安定のときだけ「途切れ」と添える
 */
export type NetSpeed = {
  level: 'fast' | 'normal' | 'slow' | 'bad';
  label: '快適' | '普通' | '遅め' | '不安定';
  why: string;
};
export const NET_FAST_MS = NET_LIMITS.fast;
export function netSpeedOf(
  quality: NetQuality | null,
  rttMs: number | null,
  lost?: number | null,
): NetSpeed | null {
  if (!quality) return null;
  if ((lost ?? 0) >= NET_LIMITS.lost) return { level: 'bad', label: '不安定', why: '途切れ' };
  if (rttMs === null) return quality === 'bad' ? { level: 'bad', label: '不安定', why: '途切れ' } : null;
  if (rttMs >= NET_LIMITS.slow) return { level: 'bad', label: '不安定', why: '' };
  if (rttMs >= NET_LIMITS.normal) return { level: 'slow', label: '遅め', why: '' };
  if (rttMs >= NET_LIMITS.fast) return { level: 'normal', label: '普通', why: '' };
  return { level: 'fast', label: '快適', why: '' };
}
/** 目安の表（画面の説明に出す） */
export const NET_SPEED_GUIDE = [
  { label: '快適', range: '100ms未満' },
  { label: '普通', range: '100〜199ms' },
  { label: '遅め', range: '200〜399ms' },
  { label: '不安定', range: '400ms以上・途切れ' },
] as const;
export const NET_SPEED_NOTE =
  '数字は、スマホとサーバーの間を往復する時間です（直近で遅かったときの値。たまに1回だけ遅いのは除きます）。小さいほど、押した順番が正確に判定されます';
